import React from "react";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import { Overline } from "@/components/blocks";
import EnquiryForm from "@/components/EnquiryForm";
import { tel, mailto } from "@/data/site";
import { useContent } from "@/context/ContentContext";

export default function Contact() {
  const CLINIC = useContent();
  const mapQuery = encodeURIComponent("Vibe Mall, Sector 110, Noida, Uttar Pradesh, India");
  return (
    <div>
      <SEO title="Contact | Dr. Mithee's Clinic for Women, Noida" path="/contact" description="Contact Dr. Mithee's Clinic for Women in Sector-110, Noida. Send an enquiry, call the clinic or find us on the map." />

      <section className="bg-gradient-to-b from-blush to-background pt-28 md:pt-36">
        <div className="container-clinic pb-12 text-center">
          <Reveal>
            <Overline>Contact & Enquiry</Overline>
            <h1 className="mt-4 font-serif text-4xl leading-tight tracking-tight text-charcoal sm:text-5xl">We'd love to hear from you</h1>
            <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-taupe md:text-lg">
              Send an enquiry and our clinic team will get back to you. For urgent bookings, please call us directly.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="container-clinic pb-20 md:pb-28">
        <div className="grid gap-10 lg:grid-cols-5">
          <Reveal className="lg:col-span-3">
            <EnquiryForm />
          </Reveal>

          <Reveal delay={120} className="lg:col-span-2">
            <div className="space-y-6">
              <div className="rounded-3xl border border-soft-border bg-cream p-8">
                <h2 className="font-serif text-2xl text-charcoal">Clinic details</h2>
                <ul className="mt-6 space-y-5 text-sm">
                  <li className="flex gap-3"><MapPin className="mt-0.5 h-5 w-5 shrink-0 text-terracotta" /><span className="text-taupe">{CLINIC.address}</span></li>
                  <li className="flex gap-3"><Phone className="mt-0.5 h-5 w-5 shrink-0 text-terracotta" />
                    <span className="flex flex-col text-taupe">
                      <a href={tel(CLINIC.appointmentMobile)} data-testid="contact-appt-phone" className="hover:text-terracotta">Appointments: {CLINIC.appointmentMobile}</a>
                      <a href={tel(CLINIC.doctorMobile)} data-testid="contact-mobile-phone" className="hover:text-terracotta">Mobile: {CLINIC.doctorMobile}</a>
                    </span>
                  </li>
                  <li className="flex gap-3"><Mail className="mt-0.5 h-5 w-5 shrink-0 text-terracotta" /><a href={mailto(CLINIC.email)} data-testid="contact-email" className="break-all text-taupe hover:text-terracotta">{CLINIC.email}</a></li>
                </ul>
              </div>

              <div className="rounded-3xl border border-soft-border bg-cream p-8">
                <h2 className="flex items-center gap-2 font-serif text-2xl text-charcoal"><Clock className="h-5 w-5 text-terracotta" /> Consultation timings</h2>
                <ul className="mt-6 space-y-3 text-sm">
                  {CLINIC.timingsList.map((t) => (
                    <li key={t.day + t.time} className="flex items-center justify-between gap-4 border-b border-soft-border pb-3 last:border-none last:pb-0">
                      <div>
                        <p className="font-medium text-charcoal">{t.day}</p>
                        {t.note && <p className="text-xs text-taupe">{t.note}</p>}
                      </div>
                      <span className="whitespace-nowrap text-right font-medium text-terracotta">{t.time}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal className="mt-12">
          <div className="overflow-hidden rounded-3xl border border-soft-border shadow-soft">
            <iframe
              title="Dr. Mithee's Clinic location — Vibe Mall, Sector 110, Noida"
              data-testid="contact-map"
              src={`https://www.google.com/maps?q=${mapQuery}&output=embed`}
              className="h-[380px] w-full"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </Reveal>
      </section>
    </div>
  );
}
